import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { GridRowParams } from '@mui/x-data-grid-pro'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { EmptyDocumentReviewQueue } from 'src/components/document-review/queue/EmptyDocumentReviewQueue'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { documentReviewPageSizeOptions } from 'src/constants/document-review/constants'
import { McQueueFields } from 'src/constants/document-review/enums'
import { buildMcQueueColumns } from 'src/constants/document-review/mcQueueGrid'
import { ADMIN_ROUTES } from 'src/constants/routes'
import type { McReviewQueueRow } from 'src/interfaces/mcDocumentReview'
import { getCustomers } from 'src/selectors/customer'
import { getMcReviewDocuments, getMcReviewDocumentsLoading } from 'src/selectors/mcDocumentReview'
import { buildCustomerNameById, filterMcQueue } from 'src/utils/documentReviewList'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  searchQuery: string
}

const getRowId = (row: McReviewQueueRow) => {
  return row.id
}

const rowPointerStyles = { '& .MuiDataGrid-row': tw`cursor-pointer` }

const McQueueTableComponent = ({ searchQuery }: Props) => {
  const navigate = useNavigate()

  const documents = useSelector(getMcReviewDocuments)
  const isLoading = useSelector(getMcReviewDocumentsLoading)
  const customers = useSelector(getCustomers)

  const { paginationModel, setPaginationModel } = useGridPagination(documentReviewPageSizeOptions)

  const customerNameById = useMemo(() => {
    return buildCustomerNameById(customers)
  }, [customers])

  // Memoised on the lookup so the grid does not remount its columns on every render.
  const columns = useMemo(() => {
    return buildMcQueueColumns(customerNameById)
  }, [customerNameById])

  const visibleDocuments = useMemo(() => {
    return filterMcQueue(documents, searchQuery, customerNameById)
  }, [documents, searchQuery, customerNameById])

  const isSearchUsed = searchQuery.trim() !== ''
  const EmptyElement = useMemo(() => {
    return () => {
      return <EmptyDocumentReviewQueue isSearchUsed={isSearchUsed} />
    }
  }, [isSearchUsed])

  const handleRowClick = useCallback(
    (params: GridRowParams<McReviewQueueRow>) => {
      navigate(
        ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_MC_URL.replace(':collectedDataId', String(params.id)),
      )
    },
    [navigate],
  )

  return (
    <GridTable<McReviewQueueRow>
      rows={visibleDocuments}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={documentReviewPageSizeOptions}
      initialState={{
        sorting: { sortModel: [{ field: McQueueFields.finishedAt, sort: 'asc' }] },
      }}
      EmptyElement={EmptyElement}
      tableDataTestId="document-review-mc-table"
      rowDataTestIdPrefix="document-review-mc"
      onRowClick={handleRowClick}
      className={rowPointerStyles}
      fillHeight
    />
  )
}

export const McQueueTable = memo(McQueueTableComponent)
